// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Content Pages (A4 paginated view + editor)
// ═══════════════════════════════════════════════════════════════

import { useRef, useEffect, useState } from 'react';
import { EditorContent } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import { PAGE, FONTS } from '../../../../../config/documentStyles';
import { C } from '../../../../../lib/design';
import { CoverPage } from './CoverPage';
import type { CoverPageProps } from './CoverPage';
import type { ContentBlock } from './renderContent';

// ─── A4 Constants ───
const A4_W = PAGE.width;
const A4_H = PAGE.height;
const A4_PAD_TOP = PAGE.padTop;
const A4_PAD_SIDE = PAGE.padSide;
const A4_PAD_BOT = PAGE.padBottom;
const PAGE_CONTENT_H = PAGE.contentHeight;

export interface ContentPagesProps {
  contentBlocks: ContentBlock[];
  editing: boolean;
  editor: Editor | null;
  zoom: number;
  coverPageProps: Omit<CoverPageProps, 'a4Style'>;
  renderPageHeader: () => React.ReactNode;
  renderPageFooter: (pageNum: number, sectionTitle: string) => React.ReactNode;
}

export function ContentPages({
  contentBlocks,
  editing,
  editor,
  zoom,
  coverPageProps,
  renderPageHeader,
  renderPageFooter,
}: ContentPagesProps) {
  const measureRef = useRef<HTMLDivElement>(null);
  const spreadRef = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState<{ blocks: ContentBlock[]; sectionTitle: string }[]>([]);
  const [containerW, setContainerW] = useState(0);

  // ─── A4 Pagination ───
  // Position-based measurement: renders all blocks together in plain marker
  // divs (margins collapse naturally), then uses offsetTop to compute
  // cumulative positions. This reflects the real rendered layout.
  useEffect(() => {
    if (contentBlocks.length === 0) {
      setPages([]);
      return;
    }
    const el = measureRef.current;
    if (!el) return;

    // Ensure container is positioned for offsetTop to work
    el.style.position = 'relative';

    el.innerHTML = '';
    const markers: HTMLDivElement[] = [];
    for (const block of contentBlocks) {
      const m = document.createElement('div');
      m.innerHTML = block.html;
      el.appendChild(m);
      markers.push(m);
    }

    requestAnimationFrame(() => {
      // Compute the bottom edge of each block using its child element
      // (the actual h1, p, ul, table, etc.)
      const bottoms: number[] = [];
      for (const m of markers) {
        const child = m.firstElementChild as HTMLElement | null;
        if (child) {
          const style = getComputedStyle(child);
          const _mt = parseFloat(style.marginTop) || 0;
          void _mt;
          const mb = parseFloat(style.marginBottom) || 0;
          // Bottom = child's top edge (offsetTop - marginTop doesn't work due to collapse)
          // Use the marker's offsetTop + child offsetHeight + child marginBottom
          bottoms.push(m.offsetTop + child.offsetHeight + mb);
        } else {
          bottoms.push(m.offsetTop + m.offsetHeight);
        }
      }

      // Use the total scrollHeight of the container as reference
      const _totalHeight = el.scrollHeight;
      void _totalHeight;

      const pageList: { blocks: ContentBlock[]; sectionTitle: string }[] = [];
      let currentPage: ContentBlock[] = [];
      let pageStart = markers[0]?.offsetTop ?? 0;
      let currentSection = '';

      for (let i = 0; i < contentBlocks.length; i++) {
        const block = contentBlocks[i];
        const blockBottom = bottoms[i] ?? 0;
        const _usedOnPage = blockBottom - pageStart;
        void _usedOnPage;

        // H1 always starts a new page (unless first block)
        if (block.isH1 && currentPage.length > 0) {
          pageList.push({ blocks: currentPage, sectionTitle: currentSection });
          currentPage = [];
          pageStart = markers[i]?.offsetTop ?? blockBottom;
        }

        // Recompute after potential H1 break
        const usedAfterBreak = bottoms[i] - pageStart;

        // Block doesn't fit → flush, start new page
        if (currentPage.length > 0 && usedAfterBreak > PAGE_CONTENT_H) {
          pageList.push({ blocks: currentPage, sectionTitle: currentSection });
          currentPage = [];
          pageStart = markers[i]?.offsetTop ?? blockBottom;
        }

        // Single oversized block → own page
        const blockH = i > 0 ? bottoms[i] - bottoms[i - 1] : bottoms[0] - pageStart;
        if (blockH > PAGE_CONTENT_H && currentPage.length === 0) {
          currentPage.push(block);
          currentSection = block.sectionTitle || currentSection;
          pageList.push({ blocks: currentPage, sectionTitle: currentSection });
          currentPage = [];
          pageStart = bottoms[i];
          continue;
        }

        currentPage.push(block);
        if (block.sectionTitle) currentSection = block.sectionTitle;
      }
      if (currentPage.length > 0) {
        pageList.push({ blocks: currentPage, sectionTitle: currentSection });
      }
      setPages(pageList);
      el.innerHTML = '';
    });
  }, [contentBlocks]);

  // ─── Track parent container width ───
  useEffect(() => {
    const el = spreadRef.current?.parentElement;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setContainerW(entry.contentRect.width);
    });
    ro.observe(el);
    setContainerW(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const scale = zoom / 100;
  const scaledW = A4_W * scale;
  const scaledH = A4_H * scale;
  const totalPages = pages.length + 1;
  const spreadGap = 24;

  // Calculate how many pages fit side by side, then constrain to exact multiple
  const availableW = containerW > 0 ? containerW - spreadGap * 2 : scaledW * 2 + spreadGap;
  const pagesPerRow = Math.max(1, Math.floor((availableW + spreadGap) / (scaledW + spreadGap)));
  const spreadContainerW = pagesPerRow * scaledW + (pagesPerRow - 1) * spreadGap + spreadGap * 2;

  // Wrapper: takes the visual scaled size so flexWrap flows correctly
  const pageWrapper = (child: React.ReactNode, key: string | number) => (
    <div key={key} style={{ width: scaledW, height: scaledH, flexShrink: 0, overflow: 'hidden' }}>
      <div
        data-a4-page
        style={{
          width: A4_W,
          height: A4_H,
          background: 'white',
          borderRadius: 2,
          boxShadow: '0 2px 16px rgba(0,0,0,0.1)',
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          overflow: 'hidden',
          boxSizing: 'border-box' as const,
          display: 'flex',
          flexDirection: 'column' as const,
        }}
      >
        {child}
      </div>
    </div>
  );

  // ─── View mode ───
  if (!editing) {
    return (
      <div
        className="a4-scroll-area"
        style={{
          background: '#DCDFE3',
          padding: '24px 0',
          minHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
        }}
      >
        {/* Hidden measuring container — uses .viewer-content CSS for accurate sizing */}
        <div
          ref={measureRef}
          className="viewer-content"
          style={{
            position: 'fixed',
            left: -9999,
            top: 0,
            visibility: 'hidden',
            width: A4_W - A4_PAD_SIDE * 2,
            fontFamily: FONTS.normative,
          }}
        />

        {/* Inner wrapper: constrains to exact N pages wide based on available width */}
        <div
          ref={spreadRef}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: spreadGap,
            alignContent: 'flex-start',
            maxWidth: spreadContainerW,
            margin: '0 auto',
            padding: `0 ${spreadGap}px`,
          }}
        >
          {/* Cover page */}
          {pageWrapper(
            <CoverPage
              {...coverPageProps}
              a4Style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}
            />,
            'cover',
          )}

          {/* Content pages */}
          {pages.length > 0
            ? pages.map((page, pageIdx) =>
                pageWrapper(
                  <>
                    {renderPageHeader()}
                    <div
                      style={{
                        flex: 1,
                        padding: `${PAGE.contentPadTop}px ${A4_PAD_SIDE}px ${PAGE.contentPadBot}px`,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        className="viewer-content"
                        dangerouslySetInnerHTML={{
                          __html: page.blocks.map((b) => b.html).join(''),
                        }}
                      />
                    </div>
                    {renderPageFooter(pageIdx + 2, page.sectionTitle)}
                  </>,
                  pageIdx,
                ),
              )
            : contentBlocks.length === 0 &&
              pageWrapper(
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <div style={{ textAlign: 'center', color: C.textMut, fontSize: 14 }}>
                    Sin contenido disponible
                  </div>
                </div>,
                'empty',
              )}
        </div>

        {/* Page count */}
        <div style={{ textAlign: 'center', padding: '8px 0 16px', fontSize: 12, color: '#8B92A5' }}>
          {totalPages} página{totalPages > 1 ? 's' : ''} · {zoom}%
        </div>
      </div>
    );
  }

  // ─── Edit mode ───
  return (
    <div
      style={{
        background: '#DCDFE3',
        padding: '24px',
        minHeight: 'calc(100vh - 200px)',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: A4_W,
          background: 'white',
          borderRadius: 4,
          boxShadow: '0 2px 16px rgba(0,0,0,0.1)',
          padding: `${A4_PAD_TOP}px ${A4_PAD_SIDE}px ${A4_PAD_BOT}px`,
          minHeight: A4_H,
          fontFamily: FONTS.normative,
        }}
      >
        <div className="viewer-content tiptap-editor">
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
}
